import { useCallback, useEffect, useRef, useState } from 'react';
import { actions as proofsActions } from 'src/features/proofs/proofsSlice';
import { useAppDispatch } from 'src/store';
import { IProof } from 'src/ts/interfaces';
import {
  useAssetVerificationReview,
  ReviewState
} from '../context/AssetVerificationReviewProvider';
import { reasonCodeLabel, VOA_STORAGE_VERSION } from '../constants';
import {
  logNoAssetDocuments,
  logPanelStateRestored,
  logStoredStateDiscarded
} from '../observability';
import { assetProofs, buildPanelState, readStoredState, voaStorageKey } from '../utils';
import { useAssetReviewLoad } from './useAssetReviewLoad';

// Shared by the flow page and the summary page: stored session state wins (it holds
// the analyst's edits); otherwise the panel seeds from the entry's asset proofs,
// pre-filled from the api response.
//
// proofsReady is the caller's "the proofs fetch has settled" flag. The review GET
// starts as soon as the provider mounts and runs alongside the proofs fetch. Only
// the seed-from-proofs branch waits on it, because that is the one building the
// document list from the proofs; a stored session already holds its documents, so
// it restores as soon as the review lands.
export const useHydrateReviewState = (entryId: string, proofs: IProof[], proofsReady: boolean) => {
  const { state, dispatch } = useAssetVerificationReview();
  const reduxDispatch = useAppDispatch();
  const { review, isLoading, isError, retry } = useAssetReviewLoad(entryId);
  const hydratedRef = useRef(false);
  // A storage-version bump discards the analyst's in-progress session. They get told,
  // because the alternative is their work vanishing on reload with no explanation.
  const [storageReset, setStorageReset] = useState(false);
  const storageKey = voaStorageKey(entryId);

  useEffect(() => {
    if (hydratedRef.current || isLoading || isError || !review) return;
    const stored = readStoredState<ReviewState>(storageKey, (storedVersion) => {
      setStorageReset(true);
      logStoredStateDiscarded(
        { entry_id: entryId, applicant_id: review.applicant_id },
        { stored_version: storedVersion, current_version: VOA_STORAGE_VERSION }
      );
    });
    if (stored && stored.documents.length > 0) {
      hydratedRef.current = true;
      dispatch({ type: 'HYDRATE', payload: { ...stored, hydratedFrom: 'storage' } });
      logPanelStateRestored(
        { entry_id: entryId, applicant_id: stored.applicantId },
        {
          source: 'storage',
          document_count: stored.documents.length,
          account_count: stored.accounts.length
        }
      );
      return;
    }
    if (!proofsReady) return;
    hydratedRef.current = true;
    const { documents, accounts, orphanDocumentCount } = buildPanelState(review, proofs);
    // FVS propagates statement-level reason codes into the review-level
    // status_details, so rendering both shows every label twice and Resolve only
    // strips the statement copy. Keep only the codes no statement carries.
    const statementCodes = new Set(
      accounts.flatMap((account) =>
        account.statements.flatMap((statement) =>
          (statement.reasonCodes ?? []).map((reason) => reason.code)
        )
      )
    );
    dispatch({
      type: 'HYDRATE',
      payload: {
        documents,
        accounts,
        applicantId: review.applicant_id,
        applicantName: review.applicant_name,
        // Mapping to detail.error and dropping the falsy ones silently discarded every
        // review-level warning against real FVS, which never populates that field.
        statusWarnings: (review.status_details ?? [])
          .filter((detail) => !statementCodes.has(detail.code))
          .map(reasonCodeLabel),
        hydratedFrom: 'api'
      }
    });
    const prefilledCount = documents.filter((doc) => doc.source === 'EXTRACTED').length;
    logPanelStateRestored(
      { entry_id: entryId, applicant_id: review.applicant_id },
      {
        source: 'api',
        document_count: documents.length,
        account_count: accounts.length,
        prefilled_document_count: prefilledCount,
        blank_document_count: documents.length - prefilledCount,
        orphan_document_count: orphanDocumentCount
      }
    );
    if (documents.length === 0) {
      logNoAssetDocuments(
        { entry_id: entryId, applicant_id: review.applicant_id },
        { proof_count: proofs.length, asset_proof_count: assetProofs(proofs).length }
      );
    }
  }, [review, isLoading, isError, storageKey, dispatch, entryId, proofs, proofsReady]);

  // Retry clears the one-shot ref and refetches the proofs, not just the review. The
  // document list is built from the proofs, so re-running the review GET on its own
  // could never change a zero-document screen. Resetting the slice puts it back to
  // IDLE, which is what makes useProofsFilteredByEntryId fetch again.
  const retryAll = useCallback(() => {
    hydratedRef.current = false;
    reduxDispatch(proofsActions.reset());
    retry();
  }, [reduxDispatch, retry]);

  return {
    state,
    isLoading: isLoading || (!isError && state.hydratedFrom === null),
    isError,
    storageReset,
    retry: retryAll
  };
};

export default useHydrateReviewState;
